import {
  Avatar,
  Chip,
  Division,
  EmptyState,
  Flex,
  Heading,
  Icons,
  Paragraph,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import type { BrandUser } from "@src/domain/entities/brand";
import type { CSSProperties } from "react";
import { BrandUserStatusUpdate } from "./BrandUserStatusUpdate";

type RoleChipConfig = {
  color?: "success" | "grey" | "warning" | "primary" | "secondary" | "error";
  style?: CSSProperties;
};

const ROLE_CHIP_CONFIG: Record<string, RoleChipConfig> = {
  "brand-admin": { color: "success" },
  approver: {
    color: "grey",
    style: {
      backgroundColor: "rgba(79, 138, 194, 0.1)",
      color: "rgba(79, 138, 194, 1)",
    },
  },
  analyst: {
    color: "grey",
    style: {
      backgroundColor: "rgba(139, 139, 224, 0.12)",
      color: "rgba(139, 139, 224, 1)",
    },
  },
  management: { color: "warning" },
  reviewer: {
    color: "grey",
    style: {
      backgroundColor: "rgba(64, 7, 211, 0.2)",
      color: "rgba(64, 7, 211, 0.7)",
    },
  },
};

const getRoleChipConfig = (roleKey: string): RoleChipConfig => {
  return ROLE_CHIP_CONFIG[roleKey] ?? { color: "grey" };
};

interface BrandDetailsUsersContentProps {
  brandId: number;
  brandUsers: BrandUser[] | null;
  onUserStatusUpdated?: (user: BrandUser) => void;
}

export const BrandDetailsUsersContent = ({
  brandId,
  brandUsers,
  onUserStatusUpdated,
}: BrandDetailsUsersContentProps) => {
  return (
    <ErrorBoundary>
      {brandUsers && brandUsers.length > 0 ? (
        brandUsers?.map((brandUser) => {
          const brandUserRoles = brandUser.brandUserRoles ?? [];

          return (
            <Flex
              key={brandUser.brandUserId}
              align="center"
              className="py-2 mb-1"
            >
              <Avatar size="small" name={brandUser.brandUserName} />
              <Division style={{ marginLeft: "1rem", flex: 1 }}>
                <Paragraph
                  className="text-grey-700 font-bold m-0"
                  style={{ lineHeight: "1" }}
                >
                  {brandUser.brandUserName}
                </Paragraph>
                <Flex align="center" gap={0.5} className="mt-1 flex-wrap">
                  {brandUserRoles.map((role) => {
                    const roleChipConfig = getRoleChipConfig(
                      role.brandUserRoleKey,
                    );

                    return (
                      <Chip
                        key={role.brandUserRoleId}
                        size="small"
                        borderless
                        label={role.brandUserRoleName}
                        color={roleChipConfig.color}
                        style={roleChipConfig.style}
                      />
                    );
                  })}
                </Flex>
              </Division>
              <Division>
                <BrandUserStatusUpdate
                  brandId={brandId}
                  brandUserId={brandUser.brandUserId}
                  brandUserIsActive={brandUser.brandUserIsActive}
                  rowBrandUser={brandUser}
                  onUserStatusUpdated={onUserStatusUpdated}
                />
              </Division>
            </Flex>
          );
        })
      ) : (
        <EmptyState
          className="bg-white border-1 border-color-grey-25 px-4 py-5 m-4"
          icon={Icons.InfoOctagon}
          title={
            <Heading level={6} className="text-grey-700 font-bold mt-3">
              No active user assigned to this brand.
            </Heading>
          }
        />
      )}
    </ErrorBoundary>
  );
};
